export function memberLabel(member) {
  return `${member.firstName} ${member.surname}`.trim();
}

export function getRelationshipConnections(members, memberPositions, cardWidth, cardHeight) {
  const connections = [];
  const childrenByFamily = new Map();

  members.forEach((member) => {
    const child = memberPositions.get(member.id);
    if (!child) return;
    const parents = (member.parentIds || [])
      .filter((parentId) => {
        const parent = memberPositions.get(parentId);
        return parent && parent.rowIndex < child.rowIndex;
      })
      .sort();
    if (!parents.length) return;
    const key = JSON.stringify([parents, child.rowIndex]);
    if (!childrenByFamily.has(key)) childrenByFamily.set(key, { parents, children: [] });
    childrenByFamily.get(key).children.push(child);
  });

  childrenByFamily.forEach(({ parents, children }) => {
    const parentPositions = parents.map((parentId) => memberPositions.get(parentId));
    const parentBottom = Math.max(...parentPositions.map((parent) => parent.top + cardHeight));
    const childTop = Math.min(...children.map((child) => child.top));
    const joinY = parentBottom + (childTop - parentBottom) / 2;
    const parentCenter = parentPositions.reduce((sum, parent) => sum + parent.x, 0) / parentPositions.length;

    parentPositions.forEach((parent, index) => {
      connections.push({
        type: 'parent',
        key: `parent-stem-${parents[index]}`,
        d: `M ${parent.x} ${parent.top + cardHeight} V ${joinY}`,
      });
    });
    if (parentPositions.length > 1) {
      connections.push({
        type: 'parent',
        key: `parent-join-${parents.join(':')}`,
        d: `M ${Math.min(...parentPositions.map((parent) => parent.x))} ${joinY} H ${Math.max(...parentPositions.map((parent) => parent.x))}`,
      });
    }

    if (children.length > 1) {
      const siblingY = childTop - 16;
      connections.push({
        type: 'parent',
        key: `parent-trunk-${parents.join(':')}`,
        d: `M ${parentCenter} ${joinY} V ${siblingY}`,
      });
      connections.push({
        type: 'sibling',
        key: `siblings-${parents.join(':')}-${children[0].rowIndex}`,
        d: `M ${Math.min(...children.map((child) => child.x))} ${siblingY} H ${Math.max(...children.map((child) => child.x))}`,
      });
      children.forEach((child, index) => {
        connections.push({
          type: 'sibling',
          key: `sibling-stem-${parents.join(':')}-${index}`,
          d: `M ${child.x} ${siblingY} V ${child.top}`,
        });
      });
    } else {
      const child = children[0];
      const childApproachY = child.top - 16;
      connections.push({
        type: 'parent',
        key: `parent-child-${parents.join(':')}-${child.x}`,
        d: `M ${parentCenter} ${joinY} V ${childApproachY} H ${child.x} V ${child.top}`,
      });
    }
  });

  const spousePairs = new Set();
  members.forEach((member) => {
    (member.spouseIds || []).forEach((spouseId) => {
      const spouse = memberPositions.get(spouseId);
      const position = memberPositions.get(member.id);
      if (!position || !spouse || position.rowIndex !== spouse.rowIndex) return;
      const [firstId, secondId] = [member.id, spouseId].sort();
      const pairKey = `${firstId}:${secondId}`;
      if (spousePairs.has(pairKey)) return;
      spousePairs.add(pairKey);
      const left = position.x < spouse.x ? position : spouse;
      const right = position.x < spouse.x ? spouse : position;
      const y = left.top + cardHeight / 2;
      connections.push({
        type: 'spouse',
        key: `spouse-${pairKey}`,
        d: `M ${left.x + cardWidth / 2} ${y} H ${right.x - cardWidth / 2}`,
      });
    });
  });

  return connections;
}

export function getGenerationRows(members) {
  const byId = new Map(members.map((member) => [member.id, member]));
  const memberIds = [...byId.keys()].sort();
  const parentEdges = [];
  members.forEach((member) => {
    (member.parentIds || []).filter((id) => byId.has(id)).forEach((parentId) => {
      parentEdges.push({ from: parentId, to: member.id, delta: 1 });
    });
  });

  const childrenByParent = new Map(memberIds.map((id) => [id, []]));
  parentEdges.forEach(({ from, to }) => childrenByParent.get(from).push(to));
  const isAncestorOf = (ancestorId, descendantId) => {
    const pending = [ancestorId];
    const visited = new Set();
    while (pending.length) {
      const currentId = pending.pop();
      if (currentId === descendantId) return true;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      pending.push(...childrenByParent.get(currentId));
    }
    return false;
  };

  const spouseEdges = [];
  const spousePairs = new Set();
  members.forEach((member) => {
    (member.spouseIds || []).filter((id) => byId.has(id)).forEach((spouseId) => {
      const [first, second] = [member.id, spouseId].sort();
      const key = `${first}:${second}`;
      if (spousePairs.has(key) || isAncestorOf(first, second) || isAncestorOf(second, first)) return;
      spousePairs.add(key);
      spouseEdges.push({ from: first, to: second, delta: 0 });
    });
  });

  const edges = [...parentEdges, ...spouseEdges]
    .sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to) || a.delta - b.delta);
  const generationEdges = new Map(memberIds.map((id) => [id, []]));
  edges.forEach(({ from, to, delta }) => {
    generationEdges.get(from).push({ id: to, delta });
    generationEdges.get(to).push({ id: from, delta: -delta });
  });
  generationEdges.forEach((neighbors) => neighbors.sort((a, b) => a.id.localeCompare(b.id)));

  const generations = new Map();
  const visited = new Set();
  memberIds.forEach((startId) => {
    if (visited.has(startId)) return;
    const component = [];
    const queue = [startId];
    generations.set(startId, 0);
    visited.add(startId);
    for (let queueIndex = 0; queueIndex < queue.length; queueIndex += 1) {
      const currentId = queue[queueIndex];
      component.push(currentId);
      generationEdges.get(currentId).forEach(({ id, delta }) => {
        if (visited.has(id)) return;
        generations.set(id, generations.get(currentId) + delta);
        visited.add(id);
        queue.push(id);
      });
    }
    const minimumGeneration = Math.min(...component.map((id) => generations.get(id)));
    component.forEach((id) => generations.set(id, generations.get(id) - minimumGeneration));
  });

  const treeX = new Map();
  const rows = new Map();
  members.forEach((member) => {
    const generation = generations.get(member.id);
    if (!rows.has(generation)) rows.set(generation, []);
    rows.get(generation).push(member);
  });

  const orderedRows = [...rows.entries()].sort(([a], [b]) => a - b);
  const rootMembers = orderedRows[0]?.[1].slice().sort((a, b) => memberLabel(a).localeCompare(memberLabel(b))) || [];
  const usedRoots = new Set();
  const parentPairs = new Set(members.flatMap((member) => {
    const parents = (member.parentIds || []).filter((id) => byId.has(id));
    return parents.length === 2 ? [parents.slice().sort().join(':')] : [];
  }));
  const isPartner = (first, second) => (first.spouseIds || []).includes(second.id)
    || (second.spouseIds || []).includes(first.id)
    || parentPairs.has([first.id, second.id].sort().join(':'));
  let rootCursor = 0;
  rootMembers.forEach((member) => {
    if (usedRoots.has(member.id)) return;
    const partner = rootMembers.find((candidate) =>
      candidate.id !== member.id && !usedRoots.has(candidate.id) && isPartner(member, candidate));
    const group = partner ? [member, partner] : [member];
    const center = rootCursor + (group.length - 1) / 2;
    group.forEach((person, index) => {
      treeX.set(person.id, center + index - (group.length - 1) / 2);
      usedRoots.add(person.id);
    });
    rootCursor += group.length + 1;
  });

  orderedRows.slice(1).forEach(([, rowMembers]) => {
    const families = new Map();
    rowMembers.forEach((member) => {
      let parents = (member.parentIds || []).filter((id) => treeX.has(id)).sort();
      if (!parents.length) {
        const partner = rowMembers
          .filter((candidate) => candidate.id !== member.id
            && ((member.spouseIds || []).includes(candidate.id)
              || (candidate.spouseIds || []).includes(member.id)))
          .sort((a, b) => memberLabel(a).localeCompare(memberLabel(b)))
          .find((candidate) => (candidate.parentIds || []).some((id) => treeX.has(id)));
        parents = partner ? partner.parentIds.filter((id) => treeX.has(id)).sort() : [];
      }
      const spouseIds = (member.spouseIds || [])
        .filter((id) => rowMembers.some((candidate) => candidate.id === id))
        .sort();
      const unlinkedGroup = spouseIds.length
        ? [member.id, ...spouseIds].sort().join(':')
        : member.id;
      const key = parents.join(':') || `unlinked:${unlinkedGroup}`;
      if (!families.has(key)) families.set(key, { parents, members: [] });
      families.get(key).members.push(member);
    });
    const orderedFamilies = [...families.values()].map((family) => {
      const sortedMembers = family.members.sort((a, b) => memberLabel(a).localeCompare(memberLabel(b)));
      const orderedMembers = [];
      const placed = new Set();
      sortedMembers.forEach((member) => {
        if (placed.has(member.id)) return;
        placed.add(member.id);
        orderedMembers.push(member);
        const partner = sortedMembers.find((candidate) => !placed.has(candidate.id)
          && ((member.spouseIds || []).includes(candidate.id)
            || (candidate.spouseIds || []).includes(member.id)));
        if (partner) {
          placed.add(partner.id);
          orderedMembers.push(partner);
        }
      });
      return {
        ...family,
        members: orderedMembers,
        center: family.parents.length
          ? family.parents.reduce((sum, parentId) => sum + treeX.get(parentId), 0) / family.parents.length
          : Infinity,
      };
    }).sort((a, b) => a.center - b.center || memberLabel(a.members[0]).localeCompare(memberLabel(b.members[0])));
    let rightmost = -Infinity;
    orderedFamilies.forEach((family) => {
      const count = family.members.length;
      const desiredLeft = family.center === Infinity
        ? (rightmost === -Infinity ? 0 : rightmost + 1)
        : family.center - (count - 1) / 2;
      const left = rightmost === -Infinity ? desiredLeft : Math.max(desiredLeft, rightmost + 1);
      family.members.forEach((member, index) => treeX.set(member.id, left + index));
      rightmost = left + count - 1;
    });
  });

  const minX = Math.min(0, ...treeX.values());
  return orderedRows.map(([generation, rowMembers]) => ({
    generation,
    members: rowMembers
      .slice()
      .sort((a, b) => treeX.get(a.id) - treeX.get(b.id))
      .map((member) => ({ ...member, treeX: treeX.get(member.id) - minX })),
  }));
}
